/**
 * Notes — Personal notes and meeting summaries
 * v0.3.506 — AI Assist + Share
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import RichTextEditor from '../RichTextEditor';
import ShareSheet from '../ShareSheet';
import NotesNetworkView from './NotesNetworkView';
import { authFetch } from '../../auth';
import './NotesPage.css';

const COLORS = [
  null, '#fff8e1', '#e8f5e9', '#e3f2fd', '#fce4ec', '#f3e5f5', '#fff3e0', '#e0f2f1'
];

const PLATFORM_LIMITS = [
  { name: 'X (Twitter)', limit: 280, icon: '𝕏' },
  { name: 'BlueSky', limit: 300, icon: '☁️' },
  { name: 'Threads', limit: 500, icon: '🧵' },
  { name: 'Instagram', limit: 2200, icon: '📷' },
  { name: 'LinkedIn', limit: 3000, icon: '💼' },
  { name: 'Weibo', limit: 2000, icon: '📢' },
  { name: 'WeChat', limit: 2000, icon: '💬' },
  { name: 'Facebook', limit: 63206, icon: '📘' },
];

const AI_OPTIONS = [
  { id: 'build', label: 'Build upon existing' },
  { id: 'spell', label: 'Spell check & grammar' },
  { id: 'translate', label: 'Translate' },
  { id: 'adopt', label: 'Adopt for...' },
];

const NotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');
  const [tagGenerating, setTagGenerating] = useState(false);
  const [color, setColor] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  // AI Assist state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState('');
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pushCopied, setPushCopied] = useState(false);
  const [pushSettingsOpen, setPushSettingsOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [pushToken, setPushToken] = useState('');
  const [pushEndpoint, setPushEndpoint] = useState('');
  const [pushLoading, setPushLoading] = useState(false);
  const [googlePickerOpen, setGooglePickerOpen] = useState(false);
  const [googleFiles, setGoogleFiles] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [translateLang, setTranslateLang] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState(function() { return localStorage.getItem('nf_note_sort') || 'updated'; });
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [addTagInput, setAddTagInput] = useState('');
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [selectedTagsToDelete, setSelectedTagsToDelete] = useState(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [filterGroups, setFilterGroups] = useState([[]]);
  const [savedFilters, setSavedFilters] = useState(function() { try { return JSON.parse(localStorage.getItem('nf_saved_filters') || '[]'); } catch(e) { return []; } }); // 'grid' | 'network'
  const aiRef = useRef(null);
  const adoptRef = useRef(null);
  const translateInputRef = useRef(null);

  const loadNotes = useCallback(async () => {
    try {
      const q = search ? `?q=${encodeURIComponent(search)}` : '';
      const res = await authFetch(`/api/notes${q}`);
      if (res.ok) setNotes(await res.json());
    } catch (e) {
      console.error('Failed to load notes:', e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (aiRef.current && !aiRef.current.contains(e.target)) setAiOpen(false);
      if (adoptRef.current && !adoptRef.current.contains(e.target)) setAdoptOpen(false);
    };
    if (aiOpen || adoptOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aiOpen, adoptOpen]);

  const openNew = () => {
    setEditing({ _new: true });
    setTitle('');
    setBody('');
    setTags('');
    setColor(null);
    setPinned(false);
  };

  // Tools-sheet actions (kept as plain named handlers so the buttons stay trivial).
  const openPushSettings = async () => {
    setToolsMenuOpen(false);
    try {
      setPushLoading(true);
      setPushSettingsOpen(true);
      const res = await authFetch('/api/notes/push-token');
      const d = await res.json();
      setPushToken(d.token || '');
      setPushEndpoint(d.endpoint || '');
    } catch (_err) {} finally {
      setPushLoading(false);
    }
  };

  const openImportPicker = async () => {
    setToolsMenuOpen(false);
    try {
      setImportLoading(true);
      const res = await authFetch('/api/google/drive/files');
      const d = await res.json();
      if (d.files) setGoogleFiles(d.files);
      setGooglePickerOpen(true);
    } catch (_err) {} finally {
      setImportLoading(false);
    }
  };

  const openEdit = (note) => {
    setEditing(note);
    setTitle(note.title || '');
    setBody(note.body || '');
    setTags((note.tags || []).join(', '));
    setColor(note.color || null);
    setPinned(note.pinned || false);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      title,
      body,
      tags: tags.split(',').map(t => t.trim().replace(/[^a-zA-Z0-9\s_-]/g, '').toLowerCase()).filter(t => t.length >= 1 && t.length <= 30),
      color,
      pinned
    };

    try {
      if (editing._new) {
        const res = await authFetch('/api/notes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const created = await res.json();
          setNotes(prev => [created, ...prev]);
        }
      } else {
        const res = await authFetch(`/api/notes/${editing.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          setNotes(prev => prev.map(n => n.uid === editing.uid ? { ...n, ...payload, tags: payload.tags, updatedAt: new Date().toISOString() } : n));
        }
      }
      setEditing(null);
      // Refresh to update tag pool
      loadNotes();
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (uid) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await authFetch(`/api/notes/${uid}`, { method: 'DELETE' });
      if (res.ok) setNotes(prev => prev.filter(n => n.uid !== uid));
      if (editing && editing.uid === uid) setEditing(null);
    } catch (e) {}
  };

  const togglePin = async (note) => {
    const newPinned = !note.pinned;
    try {
      await authFetch(`/api/notes/${note.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: newPinned })
      });
      setNotes(prev => {
        const updated = prev.map(n => n.uid === note.uid ? { ...n, pinned: newPinned } : n);
        return updated.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || new Date(b.updatedAt) - new Date(a.updatedAt));
      });
    } catch (e) {}
  };

  // --- AI Assist ---

  const callAI = async (systemInstruction, userContent) => {
    const message = systemInstruction + '\n\n---\n' + userContent;
    // Dedicated one-shot completion endpoint — the agentic /chat path mangles plain
    // text tasks (tags/spellcheck/translate) to empty.
    const res = await authFetch('/api/aiassistant/text-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message })
    });
    if (!res.ok) throw new Error('AI unavailable');
    const data = await res.json();
    return data.response || '';
  };

  const handleAiAction = async (actionId) => {
    setAiOpen(false);
    if (!body.trim()) return;
    setAiLoading(true);
    setAiStatus('Working...');

    try {
      let result;
      switch (actionId) {
        case 'build':
          setAiStatus('Building upon...');
          result = await callAI(
            'You are an assistant that expands and enhances notes. Build upon the following content. Add more detail, structure, improvements, and related points. Keep the original voice and intent. Output ONLY the enhanced text.',
            body
          );
          setBody(result);
          break;
        case 'spell':
          setAiStatus('Checking...');
          result = await callAI(
            'You are a proofreader. Fix all spelling, grammar, and punctuation errors in the following text. Preserve all formatting and structure. Output ONLY the corrected text.',
            body
          );
          setBody(result);
          break;
        case 'translate':
          // Open dialog — handled below
          setTranslateOpen(true);
          setTranslateLang('');
          setTimeout(() => { if (translateInputRef.current) translateInputRef.current.focus(); }, 100);
          return;
        default:
          break;
      }
    } catch (e) {
      console.error('AI action failed:', e);
      setAiStatus('Failed — try again');
    } finally {
      setAiLoading(false);
      setTimeout(() => setAiStatus(''), 2000);
    }
  };

  const handleTranslateConfirm = async (e) => {
    e && e.preventDefault();
    var lang = translateLang.trim();
    if (!lang) return;
    setTranslateOpen(false);
    setAiLoading(true);
    setAiStatus('Translating to ' + lang + '...');
    try {
      var result = await callAI(
        'Translate the following text to ' + lang + '. Preserve all formatting and markdown. Output ONLY the translated text.',
        body
      );
      setBody(result);
    } catch (e) {
      console.error('Translate failed:', e);
      setAiStatus('Failed — try again');
    } finally {
      setAiLoading(false);
      setTimeout(() => setAiStatus(''), 2000);
    }
  };

  const handleAdoptFor = async (platform) => {
    setAdoptOpen(false);
    if (!body.trim()) return;
    setAiLoading(true);
    setAiStatus(`Adopting for ${platform.name}...`);

    try {
      const result = await callAI(
        'You are an assistant that adapts notes for social media platforms. ' +
        'Reformat the following text for ' + platform.name + ' (max ' + platform.limit + ' characters). ' +
        'Preserve the key message and tone. Output ONLY the adapted text.',
        body
      );

      // If the adapter shrinks a longer text, create a new "post" note
      const trimmed = result.trim();
      if (trimmed.length < body.trim().length && trimmed.length > 0) {
        const postPayload = {
          title: (title || 'Untitled') + '... post',
          body: trimmed,
          tags: tags.split(',').map(function(t) { return t.trim().replace(/[^a-zA-Z0-9\s_-]/g, '').toLowerCase(); }).filter(function(t) { return t.length >= 1 && t.length <= 30; }),
          color,
          pinned: false
        };
        const res = await authFetch('/api/notes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postPayload)
        });
        if (res.ok) {
          const created = await res.json();
          setNotes(prev => [created, ...prev]);
          setAiStatus('Created post note: ' + postPayload.title);
        }
      } else {
        setBody(trimmed);
        setAiStatus('Adapted for ' + platform.name);
      }
    } catch (e) {
      console.error('Adopt failed:', e);
      setAiStatus('Failed — try again');
    } finally {
      setAiLoading(false);
      setTimeout(() => setAiStatus(''), 3000);
    }
  };

  const handleCreateNote = async (text) => {
    try {
      const res = await authFetch('/api/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'From Share', body: text, color: '#fef3c7' })
      });
      if (res.ok) {
        const created = await res.json();
        setNotes(prev => [created, ...prev]);
      }
    } catch (e) {}
  };

  const handleCreateTask = async (text) => {
    try {
      await authFetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'From Notes', description: text, priority: 'medium' })
      });
    } catch (e) {}
  };

  // Collect all unique tags from existing notes
   const allExistingTags = useMemo(() => {
    var tagSet = new Set();
    notes.forEach(function(n) { (n.tags || []).forEach(function(t) { if (t) tagSet.add(t.toLowerCase()); }); });
    return Array.from(tagSet).sort();
  }, [notes]);

  // Filter notes by tag groups: AND within group, OR across groups
   var displayedNotes = useMemo(function() {
     var activeGroups = filterGroups.filter(function(g) { return g.length > 0; });
     var filtered = activeGroups.length === 0 ? notes : notes.filter(function(n) {
       var nt = (n.tags || []).map(function(t) { return t.toLowerCase(); });
       return activeGroups.some(function(group) {
         return group.every(function(t) { return nt.includes(t.toLowerCase()); });
       });
     });
     var s = sortBy;
     return [].concat(filtered).sort(function(a, b) {
       if (s === 'pinned') return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || new Date(b.updatedAt) - new Date(a.updatedAt);
       if (s === 'created') return new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0);
       if (s === 'alpha') return (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
       return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
     });
   }, [notes, filterGroups, sortBy]);

  // Auto-generate tags via AI
  const handleGenerateTags = async () => {
    var content = (title + ' ' + body).trim();
    if (!content) return;
    setTagGenerating(true);
    try {
      var result = await callAI(
        'Extract 3-5 relevant tags from the following note content. Output ONLY lowercase tags separated by commas with no other text, bullets, or formatting. Example: meeting, ideas, follow-up',
        content
      );
      // Parse tags. The model is told to return a comma list; if it returns prose
      // instead (e.g. "i checked that"), each candidate must still look like a TAG,
      // not a sentence — otherwise we'd inject the whole phrase as one tag.
      var parts = String(result || '').split(/[\n,;]+/);
      var generated = [];
      parts.forEach(function(t) {
        var clean = t
          .replace(/^[-\d.\)\s#*•]+/, '')   // strip leading bullets, numbers, etc
          .replace(/[^a-zA-Z0-9\s_-]/g, '') // remove special chars
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-');            // tags are single tokens, not phrases
        var words = clean.split('-').filter(Boolean).length;
        // 2-30 chars, contains a letter, and at most a 2-word (hyphenated) tag —
        // this drops conversational sentences like "i-checked-that" (3+ words).
        if (clean.length >= 2 && clean.length <= 30 && /[a-z]/.test(clean) && words <= 2) {
          generated.push(clean);
        }
      });
      // Merge with existing tags
      var existing = tags.split(',').map(function(t) { return t.trim().toLowerCase(); }).filter(Boolean);
      var merged = new Set([...existing, ...generated]);
      setTags(Array.from(merged).join(', '));
    } catch (e) {
      console.error('Tag generation failed:', e);
    } finally {
      setTagGenerating(false);
    }
  };

  // Toggle a tag pill in the input
  const toggleTagPill = (tag) => {
    var current = tags.split(',').map(function(t) { return t.trim(); }).filter(Boolean);
    var idx = current.findIndex(function(t) { return t.toLowerCase() === tag.toLowerCase(); });
    if (idx >= 0) { current.splice(idx, 1); } else { current.push(tag); }
    setTags(current.join(', '));
  };

  // Filter notes by tag pill click in header
  const handleTagFilter = (tag) => {
    setSearch(prev => {
      var term = tag.toLowerCase();
      return prev.toLowerCase().includes(term) ? prev.replace(new RegExp(term + '\\s*,?\\s*', 'i'), '').trim() : (prev.trim() ? prev + ', ' + tag : tag);
    });
  };

  // Add a new tag to the global tag pool (applied to any note with matching content, or orphan)
  const handleAddGlobalTag = (e) => {
    e && e.preventDefault();
    var t = addTagInput.trim().toLowerCase().replace(/[^a-zA-Z0-9\s_-]/g, '');
    if (!t || t.length > 30) return;
    setSearch(prev => prev.trim() ? prev + ', ' + t : t);
    setAddTagInput('');
    setAddTagOpen(false);
  };

  // Delete selected tags from all notes
  const handleDeleteSelectedTags = async () => {
    if (selectedTagsToDelete.size === 0) return;
    var tagsToRemove = [...selectedTagsToDelete];
    // Remove these tags from all notes that have them
    var updated = notes.map(function(n) {
      var newTags = (n.tags || []).filter(function(t) { return !tagsToRemove.includes(t); });
      if (newTags.length !== (n.tags || []).length) {
        authFetch('/api/notes/' + n.uid, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tags: newTags }) }).catch(function(){});
        return { ...n, tags: newTags };
      }
      return n;
    });
    setNotes(updated);
    setSelectedTagsToDelete(new Set());
    setConfirmDeleteOpen(false);
    setManageTagsOpen(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const now = new Date();
    const diffH = (now - dt) / 3600000;
    if (diffH < 1) return `${Math.floor(diffH * 60)}m ago`;
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const preview = (text) => {
    if (!text) return '';
    return text.length > 120 ? text.substring(0, 120) + '...' : text;
  };

  if (loading) return <div className="notes-loading">Loading notes...</div>;

  return (
    <div className="notes-page">
      <div className="notes-header">
        <h2>Notes</h2>
        <div className="notes-actions">
          <div className="notes-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              type="text"
              placeholder="Search notes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="notes-search-clear" onClick={() => setSearch('')} title="Clear search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
          <div className="notes-actions-toolbar">
           <button className={`btn-view-toggle ${viewMode !== 'grid' ? 'active' : ''}`} onClick={function() {
             if (viewMode === 'grid') setViewMode('list');
             else if (viewMode === 'list') setViewMode('network');
             else setViewMode('grid');
           }} title="Toggle view">
             {viewMode === 'grid' ? (
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
             ) : viewMode === 'list' ? (
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
             ) : (
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="5" r="2"/><circle cx="5" cy="19" r="2"/><circle cx="19" cy="12" r="2"/><line x1="12" y1="7" x2="12" y2="12"/><line x1="7" y1="17" x2="3" y2="15"/></svg>
             )}
           </button>
           <button className={`btn-tools ${toolsMenuOpen ? 'open' : ''}`} onClick={() => setToolsMenuOpen(true)} title="Tools">
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
             <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
           </button>
           <button className="btn-new-note-icon" onClick={openNew} title="New note">
             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
           </button>
          </div>
        </div>
        {allExistingTags.length > 0 && (
          <div className="notes-tag-panel">
            <div className="notes-tag-column">
              <div className="notes-tag-col-label">
                Available
                <button className="notes-tag-pm" onClick={() => setAddTagOpen(true)} title="Add tag">+</button>
                <button className="notes-tag-pm" onClick={() => setManageTagsOpen(true)} title="Manage tags">-</button>
              </div>
              <div className="notes-tag-col-pills">
                {allExistingTags.filter(t => !filterGroups.some(function(g) { return g.includes(t); })).map(t => (
                  <button key={t} className="notes-filter-pill" onClick={() => setFilterGroups(function(prev) { var ng = prev.map(function(g) { return g.slice(); }); var last = ng[ng.length - 1]; if (!last) { ng.push([t]); } else { last.push(t); } return ng; })} draggable onDragStart={function(e) { e.dataTransfer.setData('text/plain', t); }}>{t}</button>
                ))}
              </div>
            </div>
            <div className="notes-tag-column notes-tag-filter-col">
              <div className="notes-tag-col-label">
                Filter
                {savedFilters.length > 0 && (
                  <select className="notes-saved-filter-select" value="" onChange={function(e) { var val = e.target.value; if (val) { var f = savedFilters.find(function(s) { return s.name === val; }); if (f) setFilterGroups(f.groups.map(function(g) { return g.slice(); })); } }}>
                    <option value="">saved...</option>
                    {savedFilters.map(function(s) { return <option key={s.name} value={s.name}>{s.name}</option>; })}
                  </select>
                )}
                {filterGroups.some(function(g) { return g.length > 0; }) && <button className="notes-tag-save" onClick={function() { var name = prompt('Save filter as:'); if (name && name.trim()) { var n = name.trim(); var updated = savedFilters.filter(function(s) { return s.name !== n; }); updated.push({ name: n, groups: filterGroups.filter(function(g) { return g.length > 0; }).map(function(g) { return g.slice(); }) }); localStorage.setItem('nf_saved_filters', JSON.stringify(updated)); setSavedFilters(updated); } }} title="Save current filter">save</button>}
                {filterGroups.some(function(g) { return g.length > 0; }) && <button className="notes-tag-clear" onClick={() => setFilterGroups([[]])}>clear</button>}
              </div>
              <div className="notes-tag-col-pills" onDragOver={function(e) { e.preventDefault(); }} onDrop={function(e) { e.preventDefault(); var t = e.dataTransfer.getData('text/plain'); if (t) setFilterGroups(function(prev) { var ng = prev.map(function(g) { return g.slice(); }); var last = ng[ng.length - 1]; if (!last) { ng.push([t]); } else { last.push(t); } return ng; }); }}>
                {filterGroups.every(function(g) { return g.length === 0; }) && <span className="notes-tag-empty">Drag tags here to filter</span>}
                {filterGroups.map(function(group, gi) { return (
                  <span key={gi} className="notes-filter-group">
                    {gi > 0 && <span className="notes-filter-or">OR</span>}
                    <span className="notes-filter-bracket">(</span>
                    {group.map(function(t) { return (
                      <button key={t} className="notes-filter-pill active" onClick={() => setFilterGroups(function(prev) { var ng = prev.map(function(g) { return g.slice(); }); ng[gi] = ng[gi].filter(function(x) { return x !== t; }); return ng.filter(function(g) { return g.length > 0; }); if (ng.length === 0) ng.push([]); return ng; })} draggable onDragStart={function(e) { e.dataTransfer.setData('text/plain', t); }}>{t}</button>
                    ); })}
                    <span className="notes-filter-bracket">)</span>
                    {group.length > 0 && <button className="notes-filter-group-x" onClick={() => setFilterGroups(function(prev) { var ng = prev.filter(function(g, i) { return i !== gi || g.length === 0; }); if (ng.length === 0) ng.push([]); return ng; })} title="Remove group"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>}
                  </span>
                ); })}
                <button className="notes-filter-add-group" onClick={() => setFilterGroups(function(prev) { return prev.some(function(g) { return g.length === 0; }) ? prev : prev.concat([[]]); })} title="New OR group">+</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {viewMode !== 'network' && (
        <div className="notes-sort-row">
          <select className="notes-sort-select" value={sortBy} onChange={function(e) { var v = e.target.value; setSortBy(v); localStorage.setItem('nf_note_sort', v); }}>
            <option value="updated">Last updated</option>
            <option value="created">Newest first</option>
            <option value="alpha">Alphabetical</option>
            <option value="pinned">Pinned first</option>
          </select>
        </div>
      )}
       {viewMode === 'network' ? (
         <NotesNetworkView notes={displayedNotes} onOpenNote={function(uid) { var n = notes.find(function(x) { return x.uid === uid; }); if (n) openEdit(n); }} onNewNote={function() { openNew(); }} onCreateLinkedNote={function(sourceNode) { openNew(); setTags((sourceNode.tags || []).join(', ')); setTitle(sourceNode.label ? 'Related to: ' + sourceNode.label : ''); }} onClose={function() { setViewMode('grid'); }} />
       ) : viewMode === 'list' ? (
         <div className="notes-list">
           {displayedNotes.length === 0 && !editing ? (
             <div className="notes-empty"><p>No notes yet</p></div>
           ) : displayedNotes.map(function(note) {
             return (
               <div key={note.uid} className={`note-list-item ${note.pinned ? 'pinned' : ''}`} style={note.color ? {borderLeftColor: note.color} : {}} onClick={function() { openEdit(note); }}>
                 <div className="note-list-head">
                   <h3>{note.title || 'Untitled'}</h3>
                   {(note.tags || []).length > 0 && <div className="note-tags">{note.tags.slice(0, 3).map(function(t) { return <span key={t} className="note-tag">{t}</span>; })}</div>}
                   <span className="note-date">{formatDate(note.updatedAt)}</span>
                 </div>
                 <p className="note-list-body">{preview(note.body) || ' '}</p>
               </div>
             );
           })}
         </div>
       ) : (
         <>
           {displayedNotes.length === 0 && !editing && (
             <div className="notes-empty">
               <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c4a882" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
               <p>No notes yet. Create your first note!</p>
             </div>
           )}
           <div className="notes-grid">
        {displayedNotes.map(note => (
          <div
            key={note.uid}
            className={`note-card ${note.pinned ? 'pinned' : ''}`}
            style={note.color ? { background: note.color } : undefined}
            onClick={() => openEdit(note)}
          >
            <div className="note-card-header">
              <h3>{note.title || 'Untitled'}</h3>
              <div className="note-card-actions" onClick={e => e.stopPropagation()}>
                <button className={`btn-pin ${note.pinned ? 'active' : ''}`} onClick={() => togglePin(note)} title={note.pinned ? 'Unpin' : 'Pin'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={note.pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M12 2l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9z"/></svg>
                </button>
                <button className="btn-delete-note" onClick={() => deleteNote(note.uid)} title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                </button>
              </div>
            </div>
            <p className="note-card-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{preview(note.body)}</ReactMarkdown></p>
            <div className="note-card-footer">
              {(note.tags || []).length > 0 && (
                <div className="note-tags">
                  {note.tags.map(t => <span key={t} className="note-tag">{t}</span>)}
                </div>
              )}
              <span className="note-date">{formatDate(note.updatedAt)}</span>
            </div>
          </div>
        ))}
      </div>
        </>
       )}

      {/* Edit/Create Modal */}
      {editing && (
        <div className="note-modal-overlay">
          <div className="note-modal" onClick={e => e.stopPropagation()}>
            <div className="note-modal-header">
              <h3>{editing._new ? 'New Note' : 'Edit Note'}</h3>
              <button className="btn-close-modal" onClick={() => setEditing(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="note-modal-body">
              <input
                className="note-title-input"
                type="text"
                placeholder="Title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
              />
              <RichTextEditor
                value={body}
                onChange={setBody}
                placeholder="Write your note..."
                rows={8}
              />
              <div className="note-char-count">{body.length} characters</div>
              <div className="note-toolbar">
                {/* Share button */}
                <button className="note-tool-btn" onClick={() => setShareOpen(true)} title="Share">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/></svg>
                  Share
                </button>

                {/* AI Assist button */}
                <div className="note-ai-wrap" ref={aiRef}>
                  <button className="note-tool-btn note-ai-btn" onClick={() => { setAiOpen(!aiOpen); setAdoptOpen(false); }} disabled={aiLoading} title="AI Assist">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="9" r="1" fill="currentColor"/></svg>
                    {aiLoading ? aiStatus : 'AI Assist'}
                  </button>
                  {aiOpen && (
                    <div className="note-ai-dropdown">
                      <div className="note-ai-dropdown-label">AI Assistant</div>
                      {AI_OPTIONS.map(opt => (
                        <button
                          key={opt.id}
                          className="note-ai-option"
                          onClick={() => opt.id === 'adopt' ? setAdoptOpen(!adoptOpen) : handleAiAction(opt.id)}
                          disabled={aiLoading}
                        >
                          {opt.label}
                          {opt.id === 'adopt' && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Adopt sub-menu */}
                {adoptOpen && (
                  <div className="note-adopt-dropdown" ref={adoptRef}>
                    <div className="note-ai-dropdown-label">Select platform</div>
                    {PLATFORM_LIMITS.map(p => (
                      <button
                        key={p.name}
                        className="note-ai-option"
                        onClick={() => handleAdoptFor(p)}
                        disabled={aiLoading}
                      >
                        <span className="adopt-icon">{p.icon}</span>
                        <span className="adopt-name">{p.name}</span>
                        <span className="adopt-limit">{p.limit} ch</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Color picker */}
                <div className="color-picker">
                  {COLORS.map((c, i) => (
                    <button
                      key={i}
                      className={`color-swatch ${color === c ? 'active' : ''}`}
                      style={{ background: c || '#ffffff', border: c ? 'none' : '1px solid #ddd' }}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
                <label className="note-pin-toggle">
                  <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
                  Pin to top
                </label>
              </div>
              <div className="note-field">
                <label>
                  Tags
                  <button className="note-tag-ai-btn" onClick={handleGenerateTags} disabled={tagGenerating || !body.trim()} title="Auto-generate tags with AI">
                    {tagGenerating ? '...' : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="9" r="1" fill="currentColor"/></svg>}
                  </button>
                </label>
                <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="meeting, ideas, follow-up" />
                {allExistingTags.length > 0 && (
                  <div className="note-tag-pills">
                    {allExistingTags.map(t => {
                      var active = tags.split(',').map(function(s) { return s.trim().toLowerCase(); }).includes(t.toLowerCase());
                      return <button key={t} className={`note-tag-pill ${active ? 'active' : ''}`} onClick={() => toggleTagPill(t)} draggable onDragStart={function(e) { e.dataTransfer.setData('text/plain', t); }}>{t}</button>;
                    })}
                  </div>
                )}
              </div>
              {aiStatus && !aiLoading && (
                <div className="note-ai-status">{aiStatus}</div>
              )}
            </div>
            <div className="note-modal-footer">
              <button className="btn-cancel" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-save-note" onClick={save} disabled={saving || (!title.trim() && !body.trim())}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translate language dialog */}
      {translateOpen && (
        <div className="note-modal-overlay">
          <div className="note-translate-dialog" onClick={e => e.stopPropagation()}>
            <h3>Translate note</h3>
            <p>Type the language to translate this note into:</p>
            <form onSubmit={handleTranslateConfirm}>
              <input
                ref={translateInputRef}
                type="text"
                className="note-translate-input"
                placeholder="e.g. French, Spanish, Japanese..."
                value={translateLang}
                onChange={e => setTranslateLang(e.target.value)}
                autoFocus
              />
              <div className="note-translate-actions">
                <button type="button" className="btn-cancel" onClick={() => setTranslateOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save-note" disabled={!translateLang.trim() || aiLoading}>Translate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Tag Dialog */}
      {addTagOpen && (
        <div className="note-modal-overlay" onClick={() => setAddTagOpen(false)}>
          <div className="note-translate-dialog" onClick={e => e.stopPropagation()}>
            <h3>Add Tag</h3>
            <p>Add a tag to the global pool (use in search or drag onto network nodes):</p>
            <form onSubmit={handleAddGlobalTag}>
              <input ref={translateInputRef} type="text" className="note-translate-input" placeholder="tag name" value={addTagInput} onChange={e => setAddTagInput(e.target.value)} autoFocus />
              <div className="note-translate-actions">
                <button type="button" className="btn-cancel" onClick={() => setAddTagOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save-note" disabled={!addTagInput.trim()}>Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Tags Dialog */}
      {manageTagsOpen && (
        <div className="note-modal-overlay" onClick={() => setManageTagsOpen(false)}>
          <div className="note-translate-dialog" style={{maxWidth:480}} onClick={e => e.stopPropagation()}>
            <h3>Manage Tags</h3>
            <p>Select tags to delete from all notes{confirmDeleteOpen ? ' — are you sure?' : ''}:</p>
            <div className="notes-tag-filters" style={{maxHeight:200,overflowY:'auto',marginBottom:12}}>
              {allExistingTags.map(t => (
                <button key={t}
                  className={`notes-filter-pill ${selectedTagsToDelete.has(t) ? 'active' : ''}`}
                  onClick={() => setSelectedTagsToDelete(prev => { var s = new Set(prev); s.has(t) ? s.delete(t) : s.add(t); return s; })}
                  style={selectedTagsToDelete.has(t) ? {background:'#c62828',borderColor:'#c62828',color:'#fff'} : {}}
                >{t}</button>
              ))}
            </div>
            {confirmDeleteOpen ? (
              <div className="note-translate-actions">
                <button className="btn-cancel" onClick={() => { setConfirmDeleteOpen(false); setManageTagsOpen(false); setSelectedTagsToDelete(new Set()); }}>Cancel</button>
                <button className="btn-save-note" style={{background:'#c62828'}} onClick={handleDeleteSelectedTags} disabled={selectedTagsToDelete.size === 0}>Delete {selectedTagsToDelete.size} tag(s)</button>
              </div>
            ) : (
              <div className="note-translate-actions">
                <button className="btn-cancel" onClick={() => { setManageTagsOpen(false); setSelectedTagsToDelete(new Set()); }}>Cancel</button>
                <button className="btn-save-note" style={{background:'#c62828'}} onClick={() => setConfirmDeleteOpen(true)} disabled={selectedTagsToDelete.size === 0}>Remove selected</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tools sheet — Push / Import. Inline fixed overlay (same proven pattern as the
          note editor modal), so it can't be clipped or click-intercepted. Bottom sheet on mobile. */}
      {toolsMenuOpen && (
        <div className="tools-sheet-overlay" onClick={() => setToolsMenuOpen(false)}>
          <div className="tools-sheet" onClick={e => e.stopPropagation()}>
            <div className="tools-sheet-handle" />
            <button className="tools-sheet-item" type="button" onClick={openPushSettings}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
              <div className="tools-sheet-item-text">
                <span className="tools-sheet-item-title">Push</span>
                <span className="tools-sheet-item-sub">iOS Shortcuts push endpoint</span>
              </div>
            </button>
            <button className="tools-sheet-item" type="button" onClick={openImportPicker}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <div className="tools-sheet-item-text">
                <span className="tools-sheet-item-title">Import</span>
                <span className="tools-sheet-item-sub">From Google Drive</span>
              </div>
            </button>
            <button className="tools-sheet-cancel" type="button" onClick={() => setToolsMenuOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Share Sheet */}
      {shareOpen && (
        <ShareSheet
          text={(title ? '# ' + title + '\n\n' : '') + body}
          onClose={() => setShareOpen(false)}
          onCreateNote={handleCreateNote}
          onCreateTask={handleCreateTask}
        />
      )}
       {pushSettingsOpen && (
         <div className="modal-overlay" style={{zIndex:3000}} onClick={() => setPushSettingsOpen(false)}>
           <div className="modal-content" style={{maxWidth:420}} onClick={e => e.stopPropagation()}>
             <div className="modal-header">
               <h3>Push to Notes (iOS Shortcuts)</h3>
               <button className="modal-close" onClick={() => setPushSettingsOpen(false)}>×</button>
             </div>
             <div className="modal-body">
               {pushLoading ? <p>Loading...</p> : (
                 <>
                   <label style={{fontSize:12,fontWeight:600,color:'#8892b0',textTransform:'uppercase'}}>Endpoint</label>
                   <div style={{display:'flex',gap:8,marginBottom:12}}>
                     <code style={{flex:1,background:'#f5f6fa',padding:'8px 12px',borderRadius:8,fontSize:12,wordBreak:'break-all'}}>{pushEndpoint || 'Loading...'}</code>
                     <button className="btn-copy" style={{flexShrink:0}} onClick={() => { navigator.clipboard.writeText(pushEndpoint); setPushCopied(true); setTimeout(() => setPushCopied(false), 2000); }}>{pushCopied ? '✓' : 'Copy'}</button>
                   </div>
                   <label style={{fontSize:12,fontWeight:600,color:'#8892b0',textTransform:'uppercase'}}>Token</label>
                   <div style={{display:'flex',gap:8,marginBottom:16}}>
                     <code style={{flex:1,background:'#f5f6fa',padding:'8px 12px',borderRadius:8,fontSize:11,wordBreak:'break-all'}}>{pushToken ? pushToken.substring(0,40) + '...' : 'Loading...'}</code>
                     <button className="btn-copy" style={{flexShrink:0}} onClick={() => { navigator.clipboard.writeText(pushToken); setPushCopied(true); setTimeout(() => setPushCopied(false), 2000); }}>{pushCopied ? '✓' : 'Copy'}</button>
                   </div>
                   <label style={{fontSize:12,fontWeight:600,color:'#8892b0',textTransform:'uppercase'}}>iOS Shortcut Setup</label>
                   <ol style={{fontSize:12,color:'#5a6480',paddingLeft:18,lineHeight:1.8}}>
                     <li>Open Shortcuts app → + → Add Action → Web → Get Contents of URL</li>
                     <li>URL: paste the endpoint above</li>
                     <li>Method: POST</li>
                     <li>Headers: Content-Type application/json</li>
                     <li>Body: {'{'} "token": "(paste token)", "title": "Shortcut Input", "body": "Shortcut Input" {'}'}</li>
                   </ol>
                 </>
               )}
             </div>
           </div>
         </div>
       )}
       {googlePickerOpen && (
         <div className="modal-overlay" style={{zIndex:3000}} onClick={() => setGooglePickerOpen(false)}>
           <div className="modal-content" style={{maxWidth:480}} onClick={e => e.stopPropagation()}>
             <div className="modal-header">
               <h3>Import from Google Drive</h3>
               <button className="modal-close" onClick={() => setGooglePickerOpen(false)}>×</button>
             </div>
             <div className="modal-body">
               {importLoading ? <p>Loading...</p> : googleFiles.length === 0 ? (
                 <p style={{color:'#8892b0',fontSize:13}}>No Docs or Sheets found. <a href="/profile" target="_blank" style={{color:'#1E203F'}}>Connect Google</a> in your profile settings first.</p>
               ) : (
                 <div style={{maxHeight:400,overflowY:'auto'}}>
                   {googleFiles.map(function(f) {
                     return <div key={f.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #f0f0f0'}}>
                       <div style={{display:'flex',alignItems:'center',gap:8}}>
                         <span>{f.type === 'sheet' ? '📊' : '📄'}</span>
                         <div>
                           <div style={{fontSize:13,fontWeight:500}}>{f.name}</div>
                           <div style={{fontSize:11,color:'#8892b0'}}>{f.modified ? new Date(f.modified).toLocaleDateString() : ''}</div>
                         </div>
                       </div>
                       <button onClick={async function() {
                         try {
                           setImportLoading(true);
                           var r = await authFetch('/api/google/drive/import', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ fileId: f.id }) });
                           var d = await r.json();
                           if (d.content) {
                             await authFetch('/api/notes', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ title: f.name, body: d.content, tags: ['google-import'] }) });
                             loadNotes();
                             setGooglePickerOpen(false);
                           }
                         } catch(e) {} finally { setImportLoading(false); }
                       }} style={{background:'#1E203F',color:'#fff',border:'none',borderRadius:6,padding:'4px 12px',fontSize:12,cursor:'pointer'}}>
                         Import
                       </button>
                     </div>;
                   })}
                 </div>
               )}
             </div>
           </div>
         </div>
       )}
    </div>
  );
};

export default NotesPage;
